import { Action, State, StateContext, Store } from '@ngxs/store';
import { itemsPerPage } from '@shared/helpers';
import { Injectable } from '@angular/core';
import { GroupService } from '@services/group/group.service';
import { Observable } from 'rxjs';
import { mergeMap, tap } from 'rxjs/operators';
import { IResponse } from '@interfaces/response';
import { IGroup} from '@interfaces/group';
import { GroupsStateModel } from './groups-state.model';
import {
  ChangeCurrentGroupPage,
  ChangeGroupPageWithoutUrlParams,
  ChangeGroupSearchParam,
  ChangeGroupsPerPage,
  CleanGroupSearchParam,
  CleanGroupsFromState,
  CleanSelectedGroup,
  CreateGroup,
  DeleteGroup,
  FetchGroups,
  GetAllGroups,
  GetSelectedGroupById,
  SearchGroups,
  SetGroupDefaultPerPage,
  SetGroupsPerPage,
  SetSelectedGroup,
  UpdateGroup, UpdateGroupDownloadManagerToggle, UpdateGroupWiFiSettings, UpdateScreenLockToggle
} from './groups.actions';
import { Navigate} from '@ngxs/router-plugin';
import { FetchDevices } from '@store/devices/devices.actions';
import { ToggleGlobalLoader, ToggleGroupsLoader } from '@store/loader';
import { CookieService } from 'ngx-cookie';


@State<GroupsStateModel>({
  name: 'groupsState',
  defaults: {
    groupsInUse: 0,
    groups: [],
    groupsPerPage: itemsPerPage[5],
    currentPage: 1,
    searchParam: '',
    selectedGroup: null
  }
})
@Injectable()
export class GroupsState {
  constructor(
    private groupService: GroupService,
    private cookie: CookieService,
    private store: Store
  ) {
  }

  @Action(FetchGroups, {cancelUncompleted: true})
  fetchGroups(ctx: StateContext<GroupsStateModel>, {params}: FetchGroups): Observable<any> {
    const limit = ctx.getState().groupsPerPage;
    const skip = limit * (ctx.getState().currentPage - 1);
    const search = ctx.getState().searchParam;
    this.store.dispatch(new ToggleGroupsLoader(true));
    return this.groupService.getGroups({
      limit,
      skip,
      search,
      ...params
    })
      .pipe(
        tap((res: IResponse<IGroup>) => {
          const state = ctx.getState();
          ctx.setState({...state, groups: res.rows, groupsInUse: res.total});
          this.store.dispatch(new ToggleGroupsLoader(false));
        })
      );
  }

  @Action(GetAllGroups, {cancelUncompleted: true})
  getAllGroups(ctx: StateContext<GroupsStateModel>): Observable<any> {
    const state = ctx.getState();
    ctx.setState({...state, currentPage: 1, searchParam: ''});
    return ctx.dispatch(new FetchGroups());
  }

  @Action(ChangeCurrentGroupPage)
  changeCurrentPage(ctx: StateContext<GroupsStateModel>, {currentPage}: ChangeCurrentGroupPage): Observable<any> {
    const state = ctx.getState();
    ctx.setState({...state, currentPage});

    return ctx.dispatch([
      new Navigate([], {groupsPage: currentPage}, {queryParamsHandling: 'merge'}),
      new FetchGroups()
    ]);
  }

  @Action(SearchGroups)
  searchGroups(ctx: StateContext<GroupsStateModel>, {searchParam}: SearchGroups): Observable<any> {
    const state = ctx.getState();
    ctx.setState({...state, searchParam, currentPage: 1});
    const navigate = searchParam ?
      new Navigate([], {groupsSearch: searchParam, groupsPage: 1}, {queryParamsHandling: 'merge'}) :
      new Navigate([], {groupsSearch: null, groupsPage: 1}, {queryParamsHandling: 'merge'});

    return ctx.dispatch([
      navigate,
      new FetchGroups(),
    ]);
  }

  @Action(ChangeGroupsPerPage)
  changeGroupsPerPage(ctx: StateContext<GroupsStateModel>, {groupsPerPage}: ChangeGroupsPerPage): Observable<void> {
    this.setGroupsPerPage(ctx, {groupsPerPage});
    return ctx.dispatch(new FetchGroups());
  }

  @Action(SetGroupsPerPage)
  setGroupsPerPage(ctx: StateContext<GroupsStateModel>, {groupsPerPage}: ChangeGroupsPerPage): GroupsStateModel {
    this.cookie.put('groupsPerPage', groupsPerPage.toString());
    this.changeCurrentPage(ctx, {currentPage: 1});
    return ctx.patchState({groupsPerPage});
  }

  @Action(SetSelectedGroup)
  setSelectedGroup(ctx: StateContext<GroupsStateModel>, {group}: SetSelectedGroup): Observable<void> {
    ctx.setState({...ctx.getState(), selectedGroup: group});
    return ctx.dispatch(new FetchDevices({order: '-id'}));
  }

  @Action(CleanSelectedGroup)
  cleanSelectedGroup(ctx: StateContext<GroupsStateModel>): GroupsStateModel {
    return ctx.patchState({selectedGroup: null});
  }

  @Action(GetSelectedGroupById)
  getSelectedGroupById(ctx: StateContext<GroupsStateModel>, {id}: GetSelectedGroupById): Observable<IGroup> {
    return this.groupService.getGroupById(id)
      .pipe(
        tap((groupById) => this.setSelectedGroup(ctx, {group: groupById}))
      );
  }

  @Action(CreateGroup, {cancelUncompleted: true})
  createGroup(ctx: StateContext<GroupsStateModel>, {name, description, timezone, devicesId}: CreateGroup): Observable<IGroup> {
    return this.groupService.createGroup({name, description, timezone, devicesId})
      .pipe(
        tap(() => ctx.dispatch([new FetchGroups(), new FetchDevices()]))
      );
  }

  @Action(UpdateGroup, {cancelUncompleted: true})
  updateGroup(ctx: StateContext<GroupsStateModel>, {group, groupId}: UpdateGroup): Observable<void> {
    this.store.dispatch(new ToggleGlobalLoader(true));
    return this.groupService.updateGroup(group, groupId)
      .pipe(
        tap((updatedGroup) => ctx.setState({...ctx.getState(), selectedGroup: updatedGroup})),
        mergeMap(() => ctx.dispatch([new FetchGroups(), new ToggleGlobalLoader(false)]))
      );
  }

  @Action(UpdateGroupDownloadManagerToggle, {cancelUncompleted: true})
  updateGroupDownloadManagerToggle(
      ctx: StateContext<GroupsStateModel>,
      {value, groupId}: UpdateGroupDownloadManagerToggle): Observable<void> {
    this.store.dispatch(new ToggleGlobalLoader(true));
    return this.groupService.groupDownloadManagerController(groupId, value)
        .pipe(
            // switchMap(() => ctx.dispatch(new GetSelectedGroupById(groupId))),
            mergeMap(() => ctx.dispatch([new FetchGroups(), new ToggleGlobalLoader(false)]))
        );
  }

  @Action(UpdateScreenLockToggle, {cancelUncompleted: true})
  UpdateScreenLockToggle(
      ctx: StateContext<GroupsStateModel>,
      {value, groupId}: UpdateScreenLockToggle): Observable<void> {
    this.store.dispatch(new ToggleGlobalLoader(true));
    return this.groupService.groupScreenLockController(groupId, value)
        .pipe(
            mergeMap(() => ctx.dispatch([new GetSelectedGroupById(groupId), new FetchGroups(), new ToggleGlobalLoader(false)])),
        );
  }

  @Action(UpdateGroupWiFiSettings, {cancelUncompleted: true})
  updateGroupWiFiSettings(
      ctx: StateContext<GroupsStateModel>,
      {wifiSettings, groupId}: UpdateGroupWiFiSettings): Observable<void> {
    this.store.dispatch(new ToggleGlobalLoader(true));
    return this.groupService.updateGroupWiFiSettings(groupId, wifiSettings)
        .pipe(
            mergeMap(() => ctx.dispatch([new GetSelectedGroupById(groupId), new FetchGroups(), new ToggleGlobalLoader(false)])),
        );
  }

  @Action(DeleteGroup, {cancelUncompleted: true})
  deleteGroup(ctx: StateContext<GroupsStateModel>, {id}: DeleteGroup): Observable<void> {
    return this.groupService.remove(id)
      .pipe(
        tap(() => {
          const state = ctx.getState();
          const currentPage = this.getCurrentPageAfterDelete(state.currentPage, state.groupsPerPage, state.groupsInUse);

          ctx.patchState({currentPage});
          this.store.dispatch(new Navigate([], {groupsPage: currentPage}, {queryParamsHandling: 'merge'}));
        }),
        mergeMap(() => ctx.dispatch(new FetchGroups()))
      );
  }

  // is using not from device module
  @Action(SetGroupDefaultPerPage)
  setGroupDefaultPerPage(ctx: StateContext<GroupsStateModel>): void {
    ctx.patchState({groupsPerPage: itemsPerPage[5], currentPage: 1});
  }

  // is using not from device module
  @Action(ChangeGroupPageWithoutUrlParams)
  changeGroupPageWithoutUrlParams(ctx: StateContext<GroupsStateModel>, {currentPage}: ChangeGroupPageWithoutUrlParams): void {
    ctx.patchState({currentPage});
  }

  // is using not from device module
  @Action(ChangeGroupSearchParam)
  changeGroupSearchParam(ctx: StateContext<GroupsStateModel>, {searchParam}: ChangeGroupSearchParam): void {
    ctx.patchState({searchParam, currentPage: 1});
  }

  @Action(CleanGroupsFromState)
  cleanGroupsFromState(ctx: StateContext<GroupsStateModel>): void {
    ctx.patchState({searchParam: '', groups: [], currentPage: 1});
  }

  @Action(CleanGroupSearchParam)
  cleanGroupsSearchParam(ctx: StateContext<GroupsStateModel>): void {
    ctx.patchState({searchParam: ''});
  }

  private getCurrentPageAfterDelete(currentPage: number, perPage: number, total = 0, deletedCount = 1): number {
    const totalAfterDelete = Math.max(total - deletedCount, 0);
    const lastAvailablePage = Math.max(Math.ceil(totalAfterDelete / perPage), 1);

    return Math.min(currentPage, lastAvailablePage);
  }
}
